// Provides the command input editor, hints, and keyboard interactions for sheet commands.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Paper, Popper, Tooltip, Typography } from "@mui/material";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import { appScrollbarSx } from "../../../lib/scrollbarSx";
import { getCommandSegmentAtCursor, tokenizeCommandInput } from "../commandTokenization";

interface CaretCoordinates {
  left: number;
  caretTop: number;
  caretBottom: number;
  textareaTop: number;
}

interface MarkerCoordinates {
  left: number;
  top: number;
  bottom: number;
}

interface CommandPrefillRequest {
  value: string;
  selectionStart: number;
  selectionEnd: number;
  requestId: number;
}

export interface AccountSuggestionOption {
  value: string;
  label?: string;
}

interface SheetCommandEntryProps {
  onSubmit: (value: string) => boolean | Promise<boolean>;
  onUndo?: () => void;
  onRedo?: () => void;
  canUndo?: boolean;
  canRedo?: boolean;
  showUndoRedoControls?: boolean;
  placeholder?: string;
  commandSuggestions?: string[];
  accountSuggestions?: Array<string | AccountSuggestionOption>;
  lineItemReferenceSuggestions?: string[];
  editLineItemReferenceSuggestions?: string[];
  commentLineItemReferenceSuggestions?: string[];
  commentReferenceHelpByReference?: Record<string, string>;
  prefillRequest?: CommandPrefillRequest;
  readOnly?: boolean;
  showDemoCaret?: boolean;
  disableAutocompletePopup?: boolean;
}

const DEFAULT_COMMAND_SUGGESTIONS = ["debit", "credit", "bbal", "edit", "move", "delete", "help", "title", "rename", "comment", "clear"];
const ACCOUNT_REQUIRED_COMMANDS = new Set(["credit", "cr", "debit", "db", "bbal"]);
const ACCOUNT_NAME_AUTOCOMPLETE_COMMANDS = new Set(["credit", "cr", "debit", "db", "bbal", "rename"]);
const ENTRY_REFERENCE_COMMANDS = new Set(["credit", "cr", "debit", "db"]);
const ADD_COMMENT_OPTION = "Add a comment";
const MOVE_SIDE_OPTIONS: Array<"debit" | "credit"> = ["debit", "credit"];
const OPTIONAL_SIDE_OPTIONS: Array<"debit" | "credit"> = ["debit", "credit"];
const AUTOCOMPLETE_ENABLED_STORAGE_KEY = "sheet.autocomplete.enabled";
const COMMAND_HELP_BLURBS: Record<string, string> = {
  credit: "Add a credit line item",
  debit: "Add a debit line item",
  bbal: "Set beginning balance",
  edit: "Edit line-item amount/comment",
  move: "Move line item to account",
  delete: "Delete all entries for JE-Id",
  help: "Open prompt commands page",
  title: "Set document title",
  rename: "Rename an account",
  comment: "Add or update a comment",
  clear: "Clear all entries",
};

const COMMAND_EXAMPLE_HELP: Record<string, string> = {
  credit: 'ex: credit "service revenue" 100 → Adds a $100 credit to "service revenue"',
  debit: 'ex: debit "accounts receivable" 100 → Adds a $100 debit to "accounts receivable"',
  bbal: "ex: bbal cash 500 debit → Sets cash's opening debit balance to $500",
  edit: "ex: edit a.1 300 debit → Changes line a.1 to a $300 debit",
  move: 'ex: move a.1 to cash → Moves line a.1 to "cash"',
  delete: "ex: delete a → Deletes every line in journal entry a",
  help: "ex: help → Opens the prompt commands page",
  title: 'ex: title "January close" → Sets the worksheet title',
  rename: 'ex: rename cash to "cash in bank" → Renames the cash account',
  comment: 'ex: comment a "Paid invoice" → Adds a note to journal entry a',
  clear: "ex: clear → Removes all entries from the worksheet",
};

const isAddCommentSuggestionValue = (suggestionValue: string): boolean => (
  suggestionValue.trim().toLowerCase() === ADD_COMMENT_OPTION.toLowerCase()
);

const getCommandAutocompleteAnchorIndex = (inputValue: string, selectionStart: number): number => {
  const tokenized = tokenizeCommandInput(inputValue);
  const { tokens, hasTrailingWhitespace } = tokenized;
  const firstToken = (tokens[0]?.value ?? "").toLowerCase();
  const isCreditOrDebitCommand = firstToken === "credit" || firstToken === "cr" || firstToken === "debit" || firstToken === "db";
  const accountToken = tokens[1];
  const hasCompleteAccountToken = !!accountToken && (!accountToken.quoteChar || accountToken.isClosedQuote);
  const hasEntryAccountNumberToken = isCreditOrDebitCommand && !!tokens[2]?.value && tokens[2].value.startsWith(":");
  const moveAccountToken = tokens[3];
  const hasCompleteMoveAccountToken = !!moveAccountToken && (!moveAccountToken.quoteChar || moveAccountToken.isClosedQuote);
  const moveToKeyword = (tokens[2]?.value ?? "").toLowerCase();

  const stageIndex = (tokenIndex: number): number => {
    if (tokens[tokenIndex]) {
      return tokens[tokenIndex].start;
    }

    if (tokenIndex === 0) {
      return tokens[0]?.start ?? 0;
    }

    const previousToken = tokens[tokenIndex - 1];
    if (!previousToken) {
      return selectionStart;
    }

    return Math.min(previousToken.end + 1, inputValue.length);
  };

  const isCommandStage = tokens.length === 0 || (tokens.length === 1 && !hasTrailingWhitespace);
  if (isCommandStage) {
    return stageIndex(0);
  }

  const isAccountCommand = ACCOUNT_NAME_AUTOCOMPLETE_COMMANDS.has(firstToken);
  const isAccountStage = isAccountCommand
    && (tokens.length === 1 || (tokens.length === 2 && (!hasTrailingWhitespace || !hasCompleteAccountToken)));
  if (isAccountStage) {
    return stageIndex(1);
  }

  const isAccountNumberStage = isCreditOrDebitCommand
    && hasCompleteAccountToken
    && tokens.length === 3
    && !hasTrailingWhitespace
    && (tokens[2]?.value ?? "").startsWith(":");
  if (isAccountNumberStage) {
    return stageIndex(2);
  }

  const isAmountStage = isAccountCommand
    && hasCompleteAccountToken
    && (
      (tokens.length === 2 && hasTrailingWhitespace)
      || (tokens.length === 3 && !hasTrailingWhitespace)
      || (hasEntryAccountNumberToken && tokens.length === 3 && hasTrailingWhitespace)
      || (hasEntryAccountNumberToken && tokens.length === 4 && !hasTrailingWhitespace)
    );
  if (isAmountStage) {
    return stageIndex(hasEntryAccountNumberToken ? 3 : 2);
  }

  const isReferenceCommand = ENTRY_REFERENCE_COMMANDS.has(firstToken);
  const isReferenceOrCommentStage = isReferenceCommand && (
    (!hasEntryAccountNumberToken && ((tokens.length === 3 && hasTrailingWhitespace) || (tokens.length === 4 && !hasTrailingWhitespace)))
    || (hasEntryAccountNumberToken && ((tokens.length === 4 && hasTrailingWhitespace) || (tokens.length === 5 && !hasTrailingWhitespace)))
  );
  if (isReferenceOrCommentStage) {
    return stageIndex(hasEntryAccountNumberToken ? 4 : 3);
  }

  const isOptionalCommentStage = isReferenceCommand
    && (
      (!hasEntryAccountNumberToken && tokens.length === 4 && hasTrailingWhitespace)
      || (hasEntryAccountNumberToken && tokens.length === 5 && hasTrailingWhitespace)
    );
  if (isOptionalCommentStage) {
    return stageIndex(hasEntryAccountNumberToken ? 5 : 4);
  }

  const isEditReferenceStage = firstToken === "edit" && (tokens.length === 1 || (tokens.length === 2 && !hasTrailingWhitespace));
  if (isEditReferenceStage) {
    return stageIndex(1);
  }

  const isDeleteReferenceStage = firstToken === "delete" && (tokens.length === 1 || (tokens.length === 2 && !hasTrailingWhitespace));
  if (isDeleteReferenceStage) {
    return stageIndex(1);
  }

  const isEditAmountStage = firstToken === "edit" && ((tokens.length === 2 && hasTrailingWhitespace) || (tokens.length === 3 && !hasTrailingWhitespace));
  if (isEditAmountStage) {
    return stageIndex(2);
  }

  const isEditOptionalCommentStage = firstToken === "edit" && tokens.length === 3 && hasTrailingWhitespace;
  const isEditSideStage = firstToken === "edit"
    && (
      (tokens.length === 3 && hasTrailingWhitespace)
      || (tokens.length === 4 && !hasTrailingWhitespace && !tokenized.tokens[3]?.quoteChar)
    );
  if (isEditSideStage) {
    return stageIndex(3);
  }

  const isBbalSideStage = firstToken === "bbal"
    && hasCompleteAccountToken
    && tokens[2]?.value.toLowerCase() !== "delete"
    && (
      (tokens.length === 3 && hasTrailingWhitespace)
      || (tokens.length === 4 && !hasTrailingWhitespace && !tokenized.tokens[3]?.quoteChar)
    );
  if (isBbalSideStage) {
    return stageIndex(3);
  }

  const isBbalOptionalCommentAfterSideStage = firstToken === "bbal"
    && tokens.length === 4
    && hasTrailingWhitespace
    && !tokenized.tokens[3]?.quoteChar;
  if (isBbalOptionalCommentAfterSideStage) {
    return stageIndex(4);
  }

  const isBbalOptionalCommentAfterAmountStage = firstToken === "bbal"
    && tokens.length === 4
    && !!tokenized.tokens[3]?.quoteChar;
  if (isBbalOptionalCommentAfterAmountStage) {
    return stageIndex(3);
  }

  const isEditOptionalCommentAfterSideStage = firstToken === "edit"
    && tokens.length === 4
    && hasTrailingWhitespace
    && !tokenized.tokens[3]?.quoteChar;
  if (isEditOptionalCommentAfterSideStage) {
    return stageIndex(4);
  }

  const isEditOptionalCommentAfterAmountStage = firstToken === "edit"
    && tokens.length === 4
    && !!tokenized.tokens[3]?.quoteChar;
  if (isEditOptionalCommentAfterAmountStage) {
    return stageIndex(3);
  }

  if (isEditOptionalCommentStage) {
    return stageIndex(3);
  }

  const isMoveReferenceStage = firstToken === "move" && (tokens.length === 1 || (tokens.length === 2 && !hasTrailingWhitespace));
  if (isMoveReferenceStage) {
    return stageIndex(1);
  }

  const isMoveAccountStage = firstToken === "move"
    && moveToKeyword === "to"
    && (
      (tokens.length === 3 && hasTrailingWhitespace)
      || (tokens.length === 4 && (!hasTrailingWhitespace || !hasCompleteMoveAccountToken))
    );
  if (isMoveAccountStage) {
    return stageIndex(3);
  }

  const isMoveSideStage = firstToken === "move"
    && moveToKeyword === "to"
    && hasCompleteMoveAccountToken
    && ((tokens.length === 4 && hasTrailingWhitespace) || (tokens.length === 5 && !hasTrailingWhitespace));
  if (isMoveSideStage) {
    return stageIndex(4);
  }

  const isCommentReferenceStage = firstToken === "comment" && (tokens.length === 1 || (tokens.length === 2 && !hasTrailingWhitespace));
  if (isCommentReferenceStage) {
    return stageIndex(1);
  }

  const isCommentTextStage = firstToken === "comment" && tokens.length === 2 && hasTrailingWhitespace;
  if (isCommentTextStage) {
    return stageIndex(2);
  }

  return selectionStart;
};

const getAutocompleteAnchorIndex = (inputValue: string, selectionStart: number): number => {
  const segment = getCommandSegmentAtCursor(inputValue, selectionStart);
  const leadingWhitespaceLength = segment.value.length - segment.value.trimStart().length;
  const segmentValue = segment.value.trimStart();
  const segmentStart = segment.start + leadingWhitespaceLength;
  const localSelectionStart = Math.max(0, selectionStart - segmentStart);
  return segmentStart + getCommandAutocompleteAnchorIndex(segmentValue, localSelectionStart);
};

const getMarkerCoordinates = (
  mirror: HTMLDivElement,
  textValue: string,
  index: number,
): MarkerCoordinates => {
  const safeIndex = Math.max(0, Math.min(index, textValue.length));
  const textBeforeMarker = textValue.slice(0, safeIndex).replace(/\n$/u, "\n ");

  mirror.textContent = textBeforeMarker.length > 0 ? textBeforeMarker : " ";
  const marker = document.createElement("span");
  marker.textContent = "\u200b";
  mirror.appendChild(marker);

  const markerRect = marker.getBoundingClientRect();
  const coordinates = {
    left: markerRect.left,
    top: markerRect.top,
    bottom: markerRect.bottom,
  };

  mirror.removeChild(marker);
  return coordinates;
};

const getCaretCoordinates = (textarea: HTMLTextAreaElement, atEnd = false): CaretCoordinates => {
  const selectionStart = atEnd ? textarea.value.length : textarea.selectionStart ?? 0;
  const anchorIndex = atEnd ? selectionStart : getAutocompleteAnchorIndex(textarea.value, selectionStart);
  const mirror = document.createElement("div");
  const computedStyle = window.getComputedStyle(textarea);
  const textareaRect = textarea.getBoundingClientRect();

  mirror.style.position = "absolute";
  mirror.style.visibility = "hidden";
  mirror.style.top = `${textareaRect.top + window.scrollY}px`;
  mirror.style.left = `${textareaRect.left + window.scrollX}px`;
  mirror.style.whiteSpace = "pre-wrap";
  mirror.style.wordWrap = "break-word";
  mirror.style.overflow = "auto";
  mirror.style.font = computedStyle.font;
  mirror.style.fontSize = computedStyle.fontSize;
  mirror.style.fontFamily = computedStyle.fontFamily;
  mirror.style.fontWeight = computedStyle.fontWeight;
  mirror.style.lineHeight = computedStyle.lineHeight;
  mirror.style.letterSpacing = computedStyle.letterSpacing;
  mirror.style.padding = computedStyle.padding;
  mirror.style.border = computedStyle.border;
  mirror.style.boxSizing = computedStyle.boxSizing;
  mirror.style.width = computedStyle.width;
  mirror.style.height = computedStyle.height;
  document.body.appendChild(mirror);

  const caretMarker = getMarkerCoordinates(mirror, textarea.value, selectionStart);
  const commandMarker = getMarkerCoordinates(mirror, textarea.value, anchorIndex);
  const coordinates = {
    left: commandMarker.left - textarea.scrollLeft,
    caretTop: caretMarker.top - textarea.scrollTop,
    caretBottom: caretMarker.bottom - textarea.scrollTop,
    textareaTop: textareaRect.top,
  };

  document.body.removeChild(mirror);
  return coordinates;
};

export default function SheetCommandEntry({
  onSubmit,
  onUndo = () => {},
  onRedo = () => {},
  canUndo = false,
  canRedo = false,
  showUndoRedoControls = true,
  placeholder = "Enter commands to create t-accounts (use TAB to autocomplete), use a ; to batch commands if desired.",
  commandSuggestions = DEFAULT_COMMAND_SUGGESTIONS,
  accountSuggestions = [],
  lineItemReferenceSuggestions = [],
  editLineItemReferenceSuggestions = [],
  commentLineItemReferenceSuggestions = [],
  commentReferenceHelpByReference = {},
  prefillRequest,
  readOnly = false,
  showDemoCaret = false,
  disableAutocompletePopup = false,
}: SheetCommandEntryProps) {
  const openPromptHelp = useCallback(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.location.assign("/prompt-help");
  }, []);
  const isTestEnvironment = process.env.NODE_ENV === "test";
  const entryRef = useRef<HTMLTextAreaElement | null>(null);
  const [entryInputValue, setEntryInputValue] = useState("");
  const [selectionStart, setSelectionStart] = useState(0);
  const [isEntryFocused, setIsEntryFocused] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(0);
  const [caretCoordinates, setCaretCoordinates] = useState<CaretCoordinates | null>(null);
  const [demoCaretCoordinates, setDemoCaretCoordinates] = useState<MarkerCoordinates | null>(null);
  const [isAutocompleteEnabled, setIsAutocompleteEnabled] = useState<boolean>(() => {
    if (typeof window === "undefined") {
      return true;
    }

    const storedValue = window.localStorage.getItem(AUTOCOMPLETE_ENABLED_STORAGE_KEY);
    return storedValue === null ? true : storedValue === "true";
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(AUTOCOMPLETE_ENABLED_STORAGE_KEY, String(isAutocompleteEnabled));
  }, [isAutocompleteEnabled]);

  useEffect(() => {
    if (!disableAutocompletePopup) {
      return;
    }

    setCaretCoordinates(null);
  }, [disableAutocompletePopup]);

  const isMacLike = useMemo(() => {
    if (typeof navigator === "undefined") {
      return false;
    }

    return /Mac|iPhone|iPad|iPod/u.test(navigator.platform);
  }, []);

  const undoTooltipLabel = isMacLike ? "Undo (⌘Z)" : "Undo (Ctrl+Z)";
  const redoTooltipLabel = isMacLike ? "Redo (⌘⇧Z)" : "Redo (Ctrl+Y / Ctrl+Shift+Z)";

  const resolvedPlaceholder = isAutocompleteEnabled
    ? placeholder
    : placeholder.replace(/\s*\(use TAB to autocomplete\)/i, "");

  const activeCommandSegment = useMemo(
    () => getCommandSegmentAtCursor(entryInputValue, selectionStart),
    [entryInputValue, selectionStart],
  );
  const activeSegmentLeadingWhitespace = activeCommandSegment.value.length - activeCommandSegment.value.trimStart().length;
  const activeSegmentStart = activeCommandSegment.start + activeSegmentLeadingWhitespace;
  const trimmedEntryInput = activeCommandSegment.value.trimStart();
  const tokenizedInput = useMemo(() => tokenizeCommandInput(trimmedEntryInput), [trimmedEntryInput]);
  const hasTrailingWhitespace = tokenizedInput.hasTrailingWhitespace;
  const tokens = tokenizedInput.tokens.map((token) => token.value);
  const rawTokens = tokenizedInput.tokens.map((token) => token.raw);
  const accountToken = tokenizedInput.tokens[1];
  const hasCompleteAccountToken = !!accountToken && (!accountToken.quoteChar || accountToken.isClosedQuote);
  const firstToken = (tokens[0] ?? "").toLowerCase();
  const moveToToken = (tokens[2] ?? "").toLowerCase();
  const moveAccountToken = tokenizedInput.tokens[3];
  const hasCompleteMoveAccountToken = !!moveAccountToken && (!moveAccountToken.quoteChar || moveAccountToken.isClosedQuote);
  const isCreditOrDebitCommand = firstToken === "credit" || firstToken === "cr" || firstToken === "debit" || firstToken === "db";
  const hasEntryAccountNumberToken = isCreditOrDebitCommand && !!tokens[2] && tokens[2].startsWith(":");
  const entryReferenceTokenIndex = hasEntryAccountNumberToken ? 4 : 3;
  const potentialEntryCommentToken = tokenizedInput.tokens[entryReferenceTokenIndex];
  const isTypingCommentToken = !!potentialEntryCommentToken?.quoteChar;

  const isCommandStage = tokens.length === 0 || (tokens.length === 1 && !hasTrailingWhitespace);
  const isAccountStage =
    ACCOUNT_NAME_AUTOCOMPLETE_COMMANDS.has(firstToken) &&
    (tokens.length === 1 || (tokens.length === 2 && (!hasTrailingWhitespace || !hasCompleteAccountToken)));
  const isAccountNumberStage =
    isCreditOrDebitCommand
    && hasCompleteAccountToken
    && tokens.length === 3
    && !hasTrailingWhitespace
    && (tokens[2] ?? "").startsWith(":");
  const isAmountStage =
    ACCOUNT_REQUIRED_COMMANDS.has(firstToken) &&
    hasCompleteAccountToken &&
    (
      (tokens.length === 2 && hasTrailingWhitespace)
      || (tokens.length === 3 && !hasTrailingWhitespace)
      || (hasEntryAccountNumberToken && tokens.length === 3 && hasTrailingWhitespace)
      || (hasEntryAccountNumberToken && tokens.length === 4 && !hasTrailingWhitespace)
    )
    && !isAccountNumberStage;
  const isReferenceOrCommentStage =
    ENTRY_REFERENCE_COMMANDS.has(firstToken) &&
    (
      (!hasEntryAccountNumberToken && ((tokens.length === 3 && hasTrailingWhitespace) || (tokens.length === 4 && !hasTrailingWhitespace && !isTypingCommentToken)))
      || (hasEntryAccountNumberToken && ((tokens.length === 4 && hasTrailingWhitespace) || (tokens.length === 5 && !hasTrailingWhitespace)))
    );
  const isOptionalCommentStage =
    ENTRY_REFERENCE_COMMANDS.has(firstToken) &&
    (
      (!hasEntryAccountNumberToken && tokens.length === 4)
      || (hasEntryAccountNumberToken && tokens.length === 5)
    )
    && hasTrailingWhitespace
    && !isTypingCommentToken;
  const isCommentReferenceStage =
    firstToken === "comment" &&
    (tokens.length === 1 || (tokens.length === 2 && !hasTrailingWhitespace));
  const isCommentTextStage =
    firstToken === "comment" &&
    tokens.length === 2 &&
    hasTrailingWhitespace;
  const isEditReferenceStage =
    firstToken === "edit" &&
    (tokens.length === 1 || (tokens.length === 2 && !hasTrailingWhitespace));
  const isDeleteReferenceStage =
    firstToken === "delete" &&
    (tokens.length === 1 || (tokens.length === 2 && !hasTrailingWhitespace));
  const isEditAmountStage =
    firstToken === "edit" &&
    ((tokens.length === 2 && hasTrailingWhitespace) || (tokens.length === 3 && !hasTrailingWhitespace));
  const isEditOptionalCommentStage =
    firstToken === "edit" &&
    tokens.length === 3 &&
    hasTrailingWhitespace;
  const isEditSideStage =
    firstToken === "edit"
    && (
      (tokens.length === 3 && hasTrailingWhitespace)
      || (tokens.length === 4 && !hasTrailingWhitespace && !tokenizedInput.tokens[3]?.quoteChar)
    );
  const isEditOptionalCommentAfterSideStage =
    firstToken === "edit"
    && tokens.length === 4
    && hasTrailingWhitespace
    && !tokenizedInput.tokens[3]?.quoteChar;
  const isEditOptionalCommentAfterAmountStage =
    firstToken === "edit"
    && tokens.length === 4
    && !!tokenizedInput.tokens[3]?.quoteChar;
  const isBbalSideStage =
    firstToken === "bbal"
    && hasCompleteAccountToken
    && tokens[2]?.toLowerCase() !== "delete"
    && (
      (tokens.length === 3 && hasTrailingWhitespace)
      || (tokens.length === 4 && !hasTrailingWhitespace && !tokenizedInput.tokens[3]?.quoteChar)
    );
  const isBbalOptionalCommentAfterSideStage =
    firstToken === "bbal"
    && tokens.length === 4
    && hasTrailingWhitespace
    && !tokenizedInput.tokens[3]?.quoteChar;
  const isBbalOptionalCommentAfterAmountStage =
    firstToken === "bbal"
    && tokens.length === 4
    && !!tokenizedInput.tokens[3]?.quoteChar;
  const isMoveReferenceStage =
    firstToken === "move" &&
    (tokens.length === 1 || (tokens.length === 2 && !hasTrailingWhitespace));
  const isMoveAccountStage =
    firstToken === "move"
    && moveToToken === "to"
    && (
      (tokens.length === 3 && hasTrailingWhitespace)
      || (tokens.length === 4 && (!hasTrailingWhitespace || !hasCompleteMoveAccountToken))
    );
  const isMoveSideStage =
    firstToken === "move"
    && moveToToken === "to"
    && hasCompleteMoveAccountToken
    && ((tokens.length === 4 && hasTrailingWhitespace) || (tokens.length === 5 && !hasTrailingWhitespace));

  const commandSearchText = isCommandStage ? (tokens[0] ?? "").toLowerCase() : "";
  const accountSearchText = isAccountStage && tokens.length >= 2 ? (tokens[1] ?? "").toLowerCase() : "";
  const moveAccountSearchText = isMoveAccountStage && tokens.length >= 4 ? (tokens[3] ?? "").toLowerCase() : "";

  const filteredCommandSuggestions = useMemo(() => {
    if (!isCommandStage) {
      return [];
    }

    if (!commandSearchText) {
      return commandSuggestions;
    }

    return commandSuggestions.filter((command) => command.startsWith(commandSearchText));
  }, [commandSearchText, commandSuggestions, isCommandStage]);

  const normalizedAccountSuggestions = useMemo<AccountSuggestionOption[]>(
    () => accountSuggestions.map((suggestion) => (
      typeof suggestion === "string"
        ? { value: suggestion, label: suggestion }
        : { value: suggestion.value, label: suggestion.label ?? suggestion.value }
    )),
    [accountSuggestions],
  );

  const filteredAccountSuggestions = useMemo(() => {
    if (!isAccountStage || normalizedAccountSuggestions.length === 0) {
      return [];
    }

    if (!accountSearchText) {
      return normalizedAccountSuggestions;
    }

    return normalizedAccountSuggestions.filter((accountSuggestion) => {
      const normalizedValue = accountSuggestion.value.toLowerCase();
      const normalizedLabel = (accountSuggestion.label ?? accountSuggestion.value).toLowerCase();
      return normalizedValue.includes(accountSearchText) || normalizedLabel.includes(accountSearchText);
    });
  }, [accountSearchText, isAccountStage, normalizedAccountSuggestions]);

  const filteredMoveAccountSuggestions = useMemo(() => {
    if (!isMoveAccountStage || normalizedAccountSuggestions.length === 0) {
      return [];
    }

    if (!moveAccountSearchText) {
      return normalizedAccountSuggestions;
    }

    return normalizedAccountSuggestions.filter((accountSuggestion) => {
      const normalizedValue = accountSuggestion.value.toLowerCase();
      const normalizedLabel = (accountSuggestion.label ?? accountSuggestion.value).toLowerCase();
      return normalizedValue.includes(moveAccountSearchText) || normalizedLabel.includes(moveAccountSearchText);
    });
  }, [isMoveAccountStage, moveAccountSearchText, normalizedAccountSuggestions]);

  const referenceSearchText = isReferenceOrCommentStage && tokens.length > entryReferenceTokenIndex
    ? (tokens[entryReferenceTokenIndex] ?? "").toLowerCase()
    : "";
  const filteredReferenceSuggestions = useMemo(() => {
    if (!isReferenceOrCommentStage) {
      return [];
    }

    const availableReferences = referenceSearchText
      ? lineItemReferenceSuggestions.filter((reference) => reference.toLowerCase().startsWith(referenceSearchText))
      : lineItemReferenceSuggestions;

    return [ADD_COMMENT_OPTION, ...availableReferences];
  }, [isReferenceOrCommentStage, lineItemReferenceSuggestions, referenceSearchText]);

  const editReferenceSearchText = isEditReferenceStage && tokens.length >= 2 ? (tokens[1] ?? "").toLowerCase() : "";
  const filteredEditReferenceSuggestions = useMemo(() => {
    if (!isEditReferenceStage) {
      return [];
    }

    if (!editReferenceSearchText) {
      return editLineItemReferenceSuggestions;
    }

    return editLineItemReferenceSuggestions.filter((reference) => reference.toLowerCase().startsWith(editReferenceSearchText));
  }, [editLineItemReferenceSuggestions, editReferenceSearchText, isEditReferenceStage]);

  const deleteReferenceSearchText = isDeleteReferenceStage && tokens.length >= 2 ? (tokens[1] ?? "").toLowerCase() : "";
  const filteredDeleteReferenceSuggestions = useMemo(() => {
    if (!isDeleteReferenceStage) {
      return [];
    }

    if (!deleteReferenceSearchText) {
      return lineItemReferenceSuggestions;
    }

    return lineItemReferenceSuggestions.filter((reference) => reference.toLowerCase().startsWith(deleteReferenceSearchText));
  }, [deleteReferenceSearchText, isDeleteReferenceStage, lineItemReferenceSuggestions]);

  const moveReferenceSearchText = isMoveReferenceStage && tokens.length >= 2 ? (tokens[1] ?? "").toLowerCase() : "";
  const filteredMoveReferenceSuggestions = useMemo(() => {
    if (!isMoveReferenceStage) {
      return [];
    }

    if (!moveReferenceSearchText) {
      return editLineItemReferenceSuggestions;
    }

    return editLineItemReferenceSuggestions.filter((reference) => reference.toLowerCase().startsWith(moveReferenceSearchText));
  }, [editLineItemReferenceSuggestions, isMoveReferenceStage, moveReferenceSearchText]);

  const moveSideSearchText = isMoveSideStage && tokens.length >= 5 ? (tokens[4] ?? "").toLowerCase() : "";
  const filteredMoveSideSuggestions = useMemo(() => {
    if (!isMoveSideStage) {
      return [];
    }

    if (!moveSideSearchText) {
      return MOVE_SIDE_OPTIONS;
    }

    return MOVE_SIDE_OPTIONS.filter((sideOption) => sideOption.startsWith(moveSideSearchText));
  }, [isMoveSideStage, moveSideSearchText]);

  const editSideSearchText = isEditSideStage && tokens.length >= 4 ? (tokens[3] ?? "").toLowerCase() : "";
  const filteredEditSideSuggestions = useMemo(() => {
    if (!isEditSideStage) {
      return [];
    }

    const filteredSideOptions = editSideSearchText
      ? OPTIONAL_SIDE_OPTIONS.filter((sideOption) => sideOption.startsWith(editSideSearchText))
      : OPTIONAL_SIDE_OPTIONS;

    return [ADD_COMMENT_OPTION, ...filteredSideOptions];
  }, [editSideSearchText, isEditSideStage]);

  const bbalSideSearchText = isBbalSideStage && tokens.length >= 4 ? (tokens[3] ?? "").toLowerCase() : "";
  const filteredBbalSideSuggestions = useMemo(() => {
    if (!isBbalSideStage) {
      return [];
    }

    const filteredSideOptions = bbalSideSearchText
      ? OPTIONAL_SIDE_OPTIONS.filter((sideOption) => sideOption.startsWith(bbalSideSearchText))
      : OPTIONAL_SIDE_OPTIONS;

    return [ADD_COMMENT_OPTION, ...filteredSideOptions];
  }, [bbalSideSearchText, isBbalSideStage]);

  const commentReferenceSearchText = isCommentReferenceStage && tokens.length >= 2 ? (tokens[1] ?? "").toLowerCase() : "";
  const filteredCommentReferenceSuggestions = useMemo(() => {
    if (!isCommentReferenceStage) {
      return [];
    }

    if (!commentReferenceSearchText) {
      return commentLineItemReferenceSuggestions;
    }

    return commentLineItemReferenceSuggestions.filter((reference) => reference.toLowerCase().startsWith(commentReferenceSearchText));
  }, [commentLineItemReferenceSuggestions, commentReferenceSearchText, isCommentReferenceStage]);

  const amountPromptCommand = (() => {
    if (firstToken === "cr") {
      return "credit";
    }

    if (firstToken === "db") {
      return "debit";
    }

    return firstToken;
  })();

  const autocompleteMode: "command" | "account" | "move-account" | "account-advice" | "account-number-advice" | "amount-advice" | "reference-or-comment" | "optional-comment" | "comment-reference" | "comment-text" | "edit-reference" | "delete-reference" | "move-reference" | "move-side" | "edit-side" | "bbal-side" | null = (() => {
    if (!isAutocompleteEnabled) {
      return null;
    }

    if (isCommandStage && filteredCommandSuggestions.length > 0) {
      return "command";
    }

    if (isAccountStage) {
      if (filteredAccountSuggestions.length > 0) {
        return "account";
      }

      if (normalizedAccountSuggestions.length === 0) {
        return "account-advice";
      }
    }

    if (isAmountStage) {
      return "amount-advice";
    }

    if (isAccountNumberStage) {
      return "account-number-advice";
    }

    if (isEditReferenceStage && filteredEditReferenceSuggestions.length > 0) {
      return "edit-reference";
    }

    if (isDeleteReferenceStage && filteredDeleteReferenceSuggestions.length > 0) {
      return "delete-reference";
    }

    if (isMoveReferenceStage && filteredMoveReferenceSuggestions.length > 0) {
      return "move-reference";
    }

    if (isMoveAccountStage) {
      if (filteredMoveAccountSuggestions.length > 0) {
        return "move-account";
      }

      if (normalizedAccountSuggestions.length === 0) {
        return "account-advice";
      }
    }

    if (isMoveSideStage && filteredMoveSideSuggestions.length > 0) {
      return "move-side";
    }

    if (isEditSideStage && filteredEditSideSuggestions.length > 0) {
      return "edit-side";
    }

    if (isBbalSideStage && filteredBbalSideSuggestions.length > 0) {
      return "bbal-side";
    }

    if (isEditAmountStage) {
      return "amount-advice";
    }

    if (isEditOptionalCommentStage || isEditOptionalCommentAfterSideStage || isEditOptionalCommentAfterAmountStage || isBbalOptionalCommentAfterSideStage || isBbalOptionalCommentAfterAmountStage) {
      return "optional-comment";
    }

    if (isReferenceOrCommentStage) {
      return "reference-or-comment";
    }

    if (isOptionalCommentStage) {
      return "optional-comment";
    }

    if (isCommentReferenceStage && filteredCommentReferenceSuggestions.length > 0) {
      return "comment-reference";
    }

    if (isCommentTextStage) {
      return "comment-text";
    }

    return null;
  })();

  const hasCaretAnchor = !!caretCoordinates;
  const isAutocompleteOpen = !disableAutocompletePopup
    && !readOnly
    && isAutocompleteEnabled
    && isEntryFocused
    && autocompleteMode !== null
    && hasCaretAnchor;
  const showCommandHelp = autocompleteMode === "command";
  const showCommentReferenceHelp = autocompleteMode === "comment-reference";
  const showSuggestionHelp = showCommandHelp || showCommentReferenceHelp;
  const activeSuggestions = (() => {
    switch (autocompleteMode) {
      case "command":
        return filteredCommandSuggestions;
      case "account":
        return filteredAccountSuggestions;
      case "move-account":
        return filteredMoveAccountSuggestions;
      case "reference-or-comment":
        return filteredReferenceSuggestions;
      case "edit-reference":
        return filteredEditReferenceSuggestions;
      case "delete-reference":
        return filteredDeleteReferenceSuggestions;
      case "move-reference":
        return filteredMoveReferenceSuggestions;
      case "move-side":
        return filteredMoveSideSuggestions;
      case "edit-side":
        return filteredEditSideSuggestions;
      case "bbal-side":
        return filteredBbalSideSuggestions;
      case "optional-comment":
      case "comment-text":
        return [ADD_COMMENT_OPTION];
      case "comment-reference":
        return filteredCommentReferenceSuggestions;
      default:
        return [];
    }
  })();

  const getSuggestionValue = (suggestion: string | AccountSuggestionOption): string => (
    typeof suggestion === "string" ? suggestion : suggestion.value
  );

  const getSuggestionLabel = (suggestion: string | AccountSuggestionOption): string => (
    typeof suggestion === "string" ? suggestion : (suggestion.label ?? suggestion.value)
  );

  const suggestionHighlightText = (() => {
    switch (autocompleteMode) {
      case "command":
        return commandSearchText;
      case "account":
        return accountSearchText;
      case "reference-or-comment":
        return referenceSearchText;
      case "edit-reference":
        return editReferenceSearchText;
      case "delete-reference":
        return deleteReferenceSearchText;
      case "move-reference":
        return moveReferenceSearchText;
      case "move-account":
        return moveAccountSearchText;
      case "move-side":
        return moveSideSearchText;
      case "edit-side":
        return editSideSearchText;
      case "bbal-side":
        return bbalSideSearchText;
      case "comment-reference":
        return commentReferenceSearchText;
      default:
        return "";
    }
  })();

  const renderSuggestionWithHighlight = (
    suggestionLabel: string,
    shouldBypassHighlight: boolean,
  ): React.ReactNode => {
    if (shouldBypassHighlight) {
      return suggestionLabel;
    }

    if (!suggestionHighlightText) {
      return suggestionLabel;
    }

    const normalizedSuggestion = suggestionLabel.toLowerCase();
    const normalizedHighlight = suggestionHighlightText.toLowerCase();
    const matchIndex = normalizedSuggestion.indexOf(normalizedHighlight);
    if (matchIndex === -1) {
      return suggestionLabel;
    }

    const highlightLength = suggestionHighlightText.length;
    const textBeforeMatch = suggestionLabel.slice(0, matchIndex);
    const highlightedText = suggestionLabel.slice(matchIndex, matchIndex + highlightLength);
    const textAfterMatch = suggestionLabel.slice(matchIndex + highlightLength);

    return (
      <>
        {textBeforeMatch ? <Box component="span">{textBeforeMatch}</Box> : null}
        <Box component="span" sx={{ fontWeight: (theme) => theme.typography.fontWeightBold }}>{highlightedText}</Box>
        {textAfterMatch ? <Box component="span">{textAfterMatch}</Box> : null}
      </>
    );
  };

  const commandAutocompleteAnchor = useMemo(
    () => ({
      getBoundingClientRect: () => ({
        x: caretCoordinates?.left ?? 0,
        y: caretCoordinates?.textareaTop ?? 0,
        top: caretCoordinates?.textareaTop ?? 0,
        left: caretCoordinates?.left ?? 0,
        right: caretCoordinates?.left ?? 0,
        bottom: caretCoordinates?.caretBottom ?? 0,
        width: 0,
        height: Math.max((caretCoordinates?.caretBottom ?? 0) - (caretCoordinates?.textareaTop ?? 0), 0),
        toJSON: () => ({}),
      }),
    }),
    [caretCoordinates],
  );

  const syncCaretCoordinates = useCallback(() => {
    const textarea = entryRef.current;
    if (!textarea) {
      return;
    }

    const nextCoordinates = getCaretCoordinates(textarea);
    setCaretCoordinates(nextCoordinates);
  }, []);

  const applyCommentPlaceholder = () => {
    const entryAmountTokenCount = hasEntryAccountNumberToken ? 4 : 3;
    const entryReferenceTokenCount = hasEntryAccountNumberToken ? 5 : 4;
    const basePrefix = autocompleteMode === "optional-comment"
      ? `${rawTokens.slice(0, entryReferenceTokenCount).join(" ")} `
      : (autocompleteMode === "comment-text"
        ? `${rawTokens.slice(0, 2).join(" ")} `
        : `${rawTokens.slice(0, entryAmountTokenCount).join(" ")} `);

    const completedSegment = `${basePrefix}"comment"`;
    const nextValue = `${entryInputValue.slice(0, activeSegmentStart)}${completedSegment}${entryInputValue.slice(activeCommandSegment.end)}`;
    const nextSelectionStart = activeSegmentStart + basePrefix.length + 1;
    const selectionEnd = nextSelectionStart + "comment".length;

    setEntryInputValue(nextValue);
    setSelectionStart(nextSelectionStart);
    setSelectedSuggestionIndex(0);

    requestAnimationFrame(() => {
      const textarea = entryRef.current;
      if (!textarea) {
        return;
      }

      textarea.focus();
      textarea.setSelectionRange(nextSelectionStart, selectionEnd);
      syncCaretCoordinates();
    });
  };

  const applySuggestion = (suggestionInput: string | AccountSuggestionOption) => {
    const suggestion = getSuggestionValue(suggestionInput);

    if (isAddCommentSuggestionValue(suggestion)) {
      applyCommentPlaceholder();
      return;
    }

    const shouldQuoteAccountSuggestion = /\s/u.test(suggestion);
    const formattedAccountSuggestion = shouldQuoteAccountSuggestion
      ? `"${suggestion.replace(/\\/g, "\\\\").replace(/"/g, "\\\"")}"`
      : suggestion;
    const entryPrefixTokenCount = hasEntryAccountNumberToken ? 4 : 3;

    const completedSegment = (() => {
      switch (autocompleteMode) {
        case "account":
          return firstToken === "rename"
            ? `rename ${formattedAccountSuggestion} to `
            : `${rawTokens[0] ?? tokens[0] ?? ""} ${formattedAccountSuggestion} `;
        case "move-reference":
          return `move ${suggestion} to `;
        case "move-account":
          return `move ${rawTokens[1] ?? tokens[1] ?? ""} to ${formattedAccountSuggestion} `;
        case "move-side":
          return `move ${rawTokens[1] ?? tokens[1] ?? ""} to ${rawTokens[3] ?? tokens[3] ?? ""} ${suggestion} `;
        case "edit-side":
          return `edit ${rawTokens[1] ?? tokens[1] ?? ""} ${rawTokens[2] ?? tokens[2] ?? ""} ${suggestion} `;
        case "bbal-side":
          return `${rawTokens[0] ?? tokens[0] ?? "bbal"} ${rawTokens[1] ?? tokens[1] ?? ""} ${rawTokens[2] ?? tokens[2] ?? ""} ${suggestion} `;
        case "comment-reference":
          return `comment ${suggestion} `;
        case "edit-reference":
          return `edit ${suggestion} `;
        case "delete-reference":
          return `delete ${suggestion} `;
        case "reference-or-comment":
          return `${rawTokens.slice(0, entryPrefixTokenCount).join(" ")} ${suggestion} `;
        default:
          return `${suggestion} `;
      }
    })();
    const nextValue = `${entryInputValue.slice(0, activeSegmentStart)}${completedSegment}${entryInputValue.slice(activeCommandSegment.end)}`;
    const nextSelectionStart = activeSegmentStart + completedSegment.length;
    setEntryInputValue(nextValue);
    setSelectionStart(nextSelectionStart);
    setSelectedSuggestionIndex(0);

    requestAnimationFrame(() => {
      const textarea = entryRef.current;
      if (!textarea) {
        return;
      }

      textarea.focus();
      textarea.setSelectionRange(nextSelectionStart, nextSelectionStart);
      syncCaretCoordinates();
    });
  };

  const handleEntryChange = (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setEntryInputValue(event.target.value);
    setSelectionStart(event.target.selectionStart ?? event.target.value.length);
    setSelectedSuggestionIndex(0);
    syncCaretCoordinates();
  };

  const handleEntryFocus = (): void => {
    setIsEntryFocused(true);
    requestAnimationFrame(syncCaretCoordinates);
  };

  const handleEntryBlur = (): void => {
    setIsEntryFocused(false);
    setCaretCoordinates(null);
  };

  const handleEntryClick = (): void => {
    setSelectionStart(entryRef.current?.selectionStart ?? 0);
    syncCaretCoordinates();
  };

  const handleEntrySelect = (event: React.SyntheticEvent<HTMLTextAreaElement>): void => {
    setSelectionStart(event.currentTarget.selectionStart ?? 0);
    setSelectedSuggestionIndex(0);
    syncCaretCoordinates();
  };

  const handleEntryKeyUp = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    setSelectionStart(event.currentTarget.selectionStart ?? 0);
  };

  useEffect(() => {
    if (document.activeElement === entryRef.current) {
      requestAnimationFrame(syncCaretCoordinates);
    }
  }, [syncCaretCoordinates]);

  useEffect(() => {
    if (!isEntryFocused || disableAutocompletePopup) {
      return;
    }

    const scheduleSync = () => {
      requestAnimationFrame(syncCaretCoordinates);
    };

    window.addEventListener("resize", scheduleSync);
    window.addEventListener("scroll", scheduleSync, true);

    const textarea = entryRef.current;
    const resizeObserver = textarea && typeof ResizeObserver !== "undefined"
      ? new ResizeObserver(() => {
        scheduleSync();
      })
      : null;

    if (resizeObserver && textarea) {
      resizeObserver.observe(textarea);
    }

    return () => {
      window.removeEventListener("resize", scheduleSync);
      window.removeEventListener("scroll", scheduleSync, true);
      resizeObserver?.disconnect();
    };
  }, [disableAutocompletePopup, isEntryFocused, syncCaretCoordinates]);

  useLayoutEffect(() => {
    if (!prefillRequest) {
      return;
    }

    setEntryInputValue(prefillRequest.value);
    setSelectionStart(prefillRequest.selectionStart);
    setSelectedSuggestionIndex(0);

    if (readOnly) return;

    let active = true;
    let selectionFrame: number | undefined;
    let selectionTimeout: number | undefined;
    const prefillFrame = requestAnimationFrame(() => {
      const textarea = entryRef.current;
      if (!active || !textarea) {
        return;
      }

      const applySelection = () => {
        if (!active || textarea.readOnly || textarea.value !== prefillRequest.value) {
          return;
        }

        textarea.focus();
        textarea.setSelectionRange(prefillRequest.selectionStart, prefillRequest.selectionEnd);
        syncCaretCoordinates();
      };

      applySelection();
      selectionFrame = requestAnimationFrame(applySelection);
      selectionTimeout = window.setTimeout(applySelection, 0);
    });
    return () => {
      active = false;
      cancelAnimationFrame(prefillFrame);
      if (selectionFrame !== undefined) cancelAnimationFrame(selectionFrame);
      if (selectionTimeout !== undefined) window.clearTimeout(selectionTimeout);
    };
  }, [prefillRequest, readOnly, syncCaretCoordinates]);

  useLayoutEffect(() => {
    const textarea = entryRef.current;
    if (!showDemoCaret || !textarea) {
      setDemoCaretCoordinates(null);
      return;
    }
    const syncDemoCaret = () => {
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
      textarea.scrollTop = textarea.scrollHeight;
      const coordinates = getCaretCoordinates(textarea, true);
      const containerRect = textarea.parentElement!.getBoundingClientRect();
      setDemoCaretCoordinates({
        left: coordinates.left - containerRect.left,
        top: coordinates.caretTop - containerRect.top,
        bottom: coordinates.caretBottom - containerRect.top,
      });
    };
    syncDemoCaret();
    window.addEventListener("resize", syncDemoCaret);
    window.addEventListener("scroll", syncDemoCaret, true);
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(syncDemoCaret) : null;
    observer?.observe(textarea);
    return () => {
      window.removeEventListener("resize", syncDemoCaret);
      window.removeEventListener("scroll", syncDemoCaret, true);
      observer?.disconnect();
    };
  }, [entryInputValue, showDemoCaret]);

  const handleEntryKeyDown = async (event: React.KeyboardEvent<HTMLTextAreaElement>): Promise<void> => {
    if (readOnly) {
      return;
    }

    const isModifierPressed = event.ctrlKey || event.metaKey;
    const normalizedKey = event.key.toLowerCase();
    const isUndoShortcut = isModifierPressed && !event.altKey && normalizedKey === "z" && !event.shiftKey;
    const isRedoShortcut = isModifierPressed
      && !event.altKey
      && (normalizedKey === "y" || (normalizedKey === "z" && event.shiftKey));

    if (entryInputValue.trim().length === 0 && (isUndoShortcut || isRedoShortcut)) {
      event.preventDefault();

      if (isUndoShortcut && canUndo) {
        onUndo();
      }

      if (isRedoShortcut && canRedo) {
        onRedo();
      }

      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setEntryInputValue("");
      setSelectionStart(0);
      setSelectedSuggestionIndex(0);

      requestAnimationFrame(() => {
        const textarea = entryRef.current;
        if (!textarea) {
          return;
        }

        textarea.focus();
        textarea.setSelectionRange(0, 0);
        syncCaretCoordinates();
      });
      return;
    }

    if (isAutocompleteEnabled && activeSuggestions.length > 0 && event.key === "ArrowDown") {
      event.preventDefault();
      setSelectedSuggestionIndex((previousIndex) =>
        Math.min(previousIndex + 1, activeSuggestions.length - 1));
      return;
    }

    if (isAutocompleteEnabled && activeSuggestions.length > 0 && event.key === "ArrowUp") {
      event.preventDefault();
      setSelectedSuggestionIndex((previousIndex) => Math.max(previousIndex - 1, 0));
      return;
    }

    if (isAutocompleteEnabled && autocompleteMode === "command" && event.key === "F1") {
      event.preventDefault();
      const selectedSuggestion = activeSuggestions[selectedSuggestionIndex] ?? activeSuggestions[0];
      if (selectedSuggestion) {
        const command = getSuggestionValue(selectedSuggestion);
        document.getElementById(`command-help-${encodeURIComponent(command)}`)?.click();
      }
      return;
    }

    if (isAutocompleteEnabled && activeSuggestions.length > 0 && event.key === "Tab") {
      event.preventDefault();
      const selectedSuggestion = activeSuggestions[selectedSuggestionIndex] ?? activeSuggestions[0];
      if (selectedSuggestion) {
        applySuggestion(selectedSuggestion);
      }
      return;
    }

    if (isAutocompleteEnabled && autocompleteMode === "amount-advice" && event.key === "Tab") {
      event.preventDefault();
      const completedSegment = `${trimmedEntryInput} `;
      const nextValue = `${entryInputValue.slice(0, activeSegmentStart)}${completedSegment}${entryInputValue.slice(activeCommandSegment.end)}`;
      const nextSelectionStart = activeSegmentStart + completedSegment.length;
      setEntryInputValue(nextValue);
      setSelectionStart(nextSelectionStart);
      setSelectedSuggestionIndex(0);

      requestAnimationFrame(() => {
        const textarea = entryRef.current;
        if (!textarea) {
          return;
        }

        textarea.focus();
        textarea.setSelectionRange(nextSelectionStart, nextSelectionStart);
        syncCaretCoordinates();
      });
      return;
    }

    if (event.key !== "Enter" || event.shiftKey) {
      return;
    }

    event.preventDefault();

    const wasSuccessful = await Promise.resolve(onSubmit(entryInputValue));
    if (!wasSuccessful) {
      return;
    }

    setEntryInputValue("");
    setSelectionStart(0);
    setSelectedSuggestionIndex(0);

    requestAnimationFrame(() => {
      const textarea = entryRef.current;
      if (!textarea) {
        return;
      }

      textarea.focus();
      textarea.setSelectionRange(0, 0);
      syncCaretCoordinates();
    });
  };

  const popupContent = autocompleteMode === "account-advice" || autocompleteMode === "account-number-advice" || autocompleteMode === "amount-advice" ? (
    <Paper
      elevation={2}
      sx={{
        minWidth: 220,
        maxWidth: 320,
        px: 1.25,
        py: 0.75,
        borderRadius: 1,
        border: "1px dashed rgba(0, 0, 0, 0.22)",
        backgroundColor: "background.paper",
      }}
    >
      <Typography variant="caption" color="text.secondary">
        {autocompleteMode === "account-advice"
          ? "enter an account name"
          : autocompleteMode === "account-number-advice"
            ? "enter account #"
          : amountPromptCommand === "edit"
            ? "enter updated amount"
            : amountPromptCommand === "bbal"
            ? "enter beginning balance amount or delete"
            : `enter ${amountPromptCommand} amount`}
      </Typography>
    </Paper>
  ) : (
    <Paper
      elevation={4}
      sx={{
        width: showSuggestionHelp ? "fit-content" : undefined,
        minWidth: showSuggestionHelp ? 240 : 190,
        maxWidth: showCommandHelp ? "min(960px, calc(100vw - 32px))" : showSuggestionHelp ? 340 : 260,
        maxHeight: 220,
        overflowY: "auto",
        ...appScrollbarSx,
        borderRadius: 1,
        border: "1px solid rgba(0, 0, 0, 0.08)",
      }}
    >
      <Box>
        {activeSuggestions.map((suggestion, index) => (
          (() => {
            const suggestionValue = getSuggestionValue(suggestion);
            const suggestionLabel = getSuggestionLabel(suggestion);
            const isAddCommentSuggestion = isAddCommentSuggestionValue(suggestionValue);
            return (
          <Box
            key={`${suggestionValue}-${index}`}
            sx={{
              display: "grid",
              gridTemplateColumns: showCommandHelp ? "minmax(0, 1fr) auto" : "1fr",
              columnGap: showSuggestionHelp ? 1 : 0,
              alignItems: "center",
              minHeight: 24,
              px: showSuggestionHelp ? 1 : 1.25,
              py: 0,
              cursor: "pointer",
              backgroundColor: index === selectedSuggestionIndex
                ? "rgba(25, 118, 210, 0.14)"
                : "transparent",
              "&:hover": {
                backgroundColor: index === selectedSuggestionIndex
                  ? "rgba(25, 118, 210, 0.20)"
                  : "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            <Box
              role="button"
              tabIndex={-1}
              sx={{
                display: "grid",
                gridTemplateColumns: showCommandHelp
                  ? { xs: "64px minmax(0, 1fr)", md: "72px 190px minmax(360px, 1fr)" }
                  : showSuggestionHelp ? "72px auto" : "1fr",
                columnGap: showSuggestionHelp ? 1 : 0,
                alignItems: "center",
                minWidth: 0,
                cursor: "pointer",
              }}
              onMouseDown={(event) => {
                event.preventDefault();
                applySuggestion(suggestion);
              }}
            >
              <Typography
                variant="body2"
                lineHeight={1.1}
                sx={isAddCommentSuggestion ? { fontStyle: "italic" } : undefined}
              >
                {renderSuggestionWithHighlight(suggestionLabel, isAddCommentSuggestion)}
              </Typography>
              {showSuggestionHelp ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  lineHeight={1.2}
                  sx={{
                    whiteSpace: showCommandHelp ? { xs: "normal", md: "nowrap" } : "nowrap",
                    pr: 0.5,
                  }}
                >
                  {showCommandHelp
                    ? (COMMAND_HELP_BLURBS[suggestionValue] ?? "")
                    : (commentReferenceHelpByReference[suggestionValue.toLowerCase()] ?? "")}
                </Typography>
              ) : null}
              {showCommandHelp ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  lineHeight={1.2}
                  sx={{
                    gridColumn: { xs: "1 / -1", md: "auto" },
                    whiteSpace: { xs: "normal", md: "nowrap" },
                  }}
                >
                  {COMMAND_EXAMPLE_HELP[suggestionValue] ?? ""}
                </Typography>
              ) : null}
            </Box>
            {showCommandHelp ? (
              <Typography
                id={`command-help-${encodeURIComponent(suggestionValue)}`}
                component="a"
                href={`/prompt-commands#${encodeURIComponent(suggestionValue)}`}
                aria-label={`Help for ${suggestionValue}`}
                aria-keyshortcuts="F1"
                variant="caption"
                lineHeight={1.2}
                onMouseDown={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                }}
                sx={{
                  color: "primary.main",
                  whiteSpace: "nowrap",
                  textDecoration: "underline",
                }}
              >
                help
              </Typography>
            ) : null}
          </Box>
            );
          })()
        ))}
      </Box>
    </Paper>
  );

  return (
    <>
      <Box sx={{ position: "relative" }}>
        <Box
          sx={{
            position: "absolute",
            top: 2,
            right: 16,
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          {showUndoRedoControls ? (
            <>
              <Tooltip title={undoTooltipLabel}>
                <Box component="span">
                  <IconButton
                    size="small"
                    aria-label="undo"
                    disabled={!canUndo}
                    onMouseDown={(event) => {
                      event.preventDefault();
                    }}
                    onClick={onUndo}
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "7px",
                    }}
                  >
                    <UndoIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </Tooltip>

              <Tooltip title={redoTooltipLabel}>
                <Box component="span">
                  <IconButton
                    size="small"
                    aria-label="redo"
                    disabled={!canRedo}
                    onMouseDown={(event) => {
                      event.preventDefault();
                    }}
                    onClick={onRedo}
                    sx={{
                      width: 24,
                      height: 24,
                      borderRadius: "7px",
                    }}
                  >
                    <RedoIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              </Tooltip>
            </>
          ) : null}

          <Tooltip title="Prompt help">
            <Box component="span">
              <IconButton
                size="small"
                aria-label="prompt help"
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={openPromptHelp}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "7px",
                  color: "primary.main",
                  backgroundColor: "rgba(25, 118, 210, 0.16)",
                  border: "none",
                  "&:hover": {
                    backgroundColor: "rgba(25, 118, 210, 0.24)",
                  },
                }}
              >
                <Typography component="span" sx={{ fontSize: 14, fontWeight: 700, lineHeight: 1 }}>
                  ?
                </Typography>
              </IconButton>
            </Box>
          </Tooltip>

          <Tooltip title={isAutocompleteEnabled ? "Autocomplete on" : "Autocomplete off"}>
            <Box component="span">
              <IconButton
                size="small"
                aria-label="toggle autocomplete"
                aria-pressed={isAutocompleteEnabled}
                disabled={readOnly}
                onMouseDown={(event) => {
                  event.preventDefault();
                }}
                onClick={() => setIsAutocompleteEnabled((previousValue) => !previousValue)}
                sx={{
                  width: 24,
                  height: 24,
                  borderRadius: "7px",
                  color: isAutocompleteEnabled ? "primary.main" : "text.secondary",
                  backgroundColor: isAutocompleteEnabled ? "rgba(25, 118, 210, 0.16)" : "transparent",
                  border: "none",
                  "&:hover": {
                    backgroundColor: isAutocompleteEnabled ? "rgba(25, 118, 210, 0.24)" : "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <AutoFixHighIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>

        <Box
          component="textarea"
          ref={entryRef}
          autoFocus
          value={entryInputValue}
          placeholder={resolvedPlaceholder}
          readOnly={readOnly}
          aria-keyshortcuts="F1"
          aria-description="When command suggestions are open, press F1 for help about the selected command."
          onChange={handleEntryChange}
          onFocus={handleEntryFocus}
          onBlur={handleEntryBlur}
          onClick={handleEntryClick}
          onSelect={handleEntrySelect}
          onKeyDown={handleEntryKeyDown}
          onKeyUp={handleEntryKeyUp}
          sx={{
            width: "100%",
            minHeight: "44px",
            maxHeight: "44px",
            mt: 1.5,
            mb: .7,
            border: "none",
            resize: "none",
            outline: "none",
            backgroundColor: "transparent",
            py: 1,
            px: 3,
            font: "inherit",
            lineHeight: 1.4,
            color: "text.primary",
            caretColor: showDemoCaret ? "transparent" : undefined,
            boxSizing: "border-box",
            overflow: "hidden",
            "&::placeholder": {
              color: (theme) => (showDemoCaret ? "transparent" : theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.72)" : "rgba(0, 0, 0, 0.38)"),
            },
          }}
        />
        {showDemoCaret && demoCaretCoordinates ? (
          <Box
            data-testid="demo-command-caret"
            aria-hidden="true"
            sx={{
              position: "absolute",
              left: demoCaretCoordinates.left,
              top: demoCaretCoordinates.top,
              height: Math.max(16, demoCaretCoordinates.bottom - demoCaretCoordinates.top),
              width: "1px",
              bgcolor: "text.primary",
              pointerEvents: "none",
              animation: "demo-command-caret-blink 1s step-end infinite",
              "@keyframes demo-command-caret-blink": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0 },
              },
            }}
          />
        ) : null}
      </Box>

      {isAutocompleteOpen ? (
        isTestEnvironment ? (
          <Box sx={{ position: "absolute", left: 0, top: 0, zIndex: 1300 }}>{popupContent}</Box>
        ) : (
          <Popper
            open={isAutocompleteOpen}
            anchorEl={commandAutocompleteAnchor}
            placement="bottom-start"
            modifiers={[
              {
                name: "offset",
                options: {
                  offset: [0, 6],
                },
              },
              {
                name: "flip",
                options: {
                  fallbackPlacements: ["top-start", "bottom-start"],
                  padding: 8,
                },
              },
              {
                name: "preventOverflow",
                options: {
                  padding: 8,
                  altAxis: true,
                },
              },
            ]}
            sx={{ zIndex: 1300 }}
          >
            {popupContent}
          </Popper>
        )
      ) : null}
    </>
  );
}
